import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DatabaseZap, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admin card: full refresh of all datasets for all kommuner, with live progress
const FullSyncCard = () => {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/sync-all/status`, { withCredentials: true });
      setStatus(res.data);
    } catch (e) {
      console.error("Full sync status error:", e);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const running = status?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(fetchStatus, 3000);
    return () => clearInterval(id);
  }, [running, fetchStatus]);

  const start = async () => {
    try {
      setStarting(true);
      await axios.post(`${API}/admin/sync-all`, {}, { withCredentials: true });
      toast.success("Full oppdatering starta", { description: "Alle data for alle kommunar blir henta frå SSB i bakgrunnen." });
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikkje starte full oppdatering");
    } finally {
      setStarting(false);
    }
  };

  const pctDone = status?.total ? Math.round((status.done / status.total) * 100) : 0;

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="full-sync-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <DatabaseZap className="w-5 h-5 text-[#103766]" />
          Hent oppdaterte data
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm text-[#4B647F]">
              Éin knapp som hentar alt på nytt for alle kommunar: økonomi og rekneskap, eigedomsskatt, inntektskjelder,
              investeringar, driftsutgifter, gjeld og konserngjeld, arbeidskapital og fond, gebyr, bustadprisar, leige og
              bokostnader, hushaldsøkonomi, barnehage og skule, omsorgstenester, fastlegedekning, sosialhjelp, arbeid og
              ytingar, pendling og næringar, alder og framskriving, ulikskap, befolkning og demografi, nabokommunar og
              ordførarar/styringsparti frå Wikipedia.
            </p>
            <p className="text-xs text-[#8AA4BF] mt-1" data-testid="full-sync-status">
              {running
                ? `Køyrer${status.phase ? ` – ${status.phase}` : ""}: ${status.done} av ${status.total} kommunar (${pctDone} %)${status.current ? ` – nå: ${status.current}` : ""}`
                : status?.status === "interrupted"
                  ? "Førre køyring blei avbroten (server starta på nytt). Start på nytt."
                  : status?.last_sync
                    ? `Sist fullført: ${new Date(status.last_sync).toLocaleString("nb-NO")}${status.errors ? ` – ${status.errors} feil` : ""}`
                    : "Aldri køyrt"}
            </p>
            {running && (
              <div className="mt-2 h-2 w-full bg-[#E6E9F0] rounded-full overflow-hidden" data-testid="full-sync-progress">
                <div className="h-full bg-[#103766] transition-[width] duration-500" style={{ width: `${pctDone}%` }} />
              </div>
            )}
          </div>
          <Button
            onClick={start}
            disabled={running || starting}
            data-testid="full-sync-btn"
            className="bg-[#103766] hover:bg-[#1B4E8A] text-white shrink-0"
          >
            {running ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Oppdaterer...</>
            ) : (
              <><DatabaseZap className="w-4 h-4 mr-2" />Hent oppdaterte data</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FullSyncCard;
